import { ChevronDown, Info, Workflow } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { BetaBadge } from '@/components/common/BetaBadge'
import { FeedbackBadge } from '@/components/common/FeedbackBadge'
import LanguageSwitcher, { type AnalysisLanguage } from '@/components/common/language-switcher'
import { Button as UIButton } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAuth } from '@/context'
import { useTranslation } from '@/hooks/useTranslation'
import { TOUR } from '@/lib/product-tour/constants'
import { cn } from '@/lib/utils'
import { submitAnalysisLanguageRequestFeedback } from '@/services/languageRequestFeedback'
import { isSentryUserFeedbackAvailable } from '@/services/sentry'

import {
	BPMN_OPTION_GROUPS,
	type BpmnGenerationOptionKey,
	type BpmnGenerationOptionsState,
	countEnabledBpmnOptions,
} from './bpmnOptions'
import type { StepComponentProps } from './types'

interface TemplateStepProps extends StepComponentProps {
	selectedTemplate: string | null
	onTemplateSelect: (template: string | null) => void
	analysisLanguage: AnalysisLanguage
	onLanguageChange: (language: AnalysisLanguage) => void
	bpmnGenerationOptions: BpmnGenerationOptionsState
	onBpmnGenerationOptionsChange: (options: BpmnGenerationOptionsState) => void
}

export function TemplateStep({
	selectedTemplate,
	onTemplateSelect,
	analysisLanguage,
	onLanguageChange,
	bpmnGenerationOptions,
	onBpmnGenerationOptionsChange,
	onContinue,
}: TemplateStepProps) {
	const { t } = useTranslation()
	const { user } = useAuth()
	const [optionsOpen, setOptionsOpen] = useState(false)
	const [showLanguageRequest, setShowLanguageRequest] = useState(false)
	const [languageRequestText, setLanguageRequestText] = useState('')
	const [languageRequestError, setLanguageRequestError] = useState(false)
	const [languageRequestSubmitting, setLanguageRequestSubmitting] = useState(false)
	const enabledCount = countEnabledBpmnOptions(bpmnGenerationOptions)
	const languageRequestAvailable = isSentryUserFeedbackAvailable()

	const resetLanguageRequestForm = useCallback(() => {
		setShowLanguageRequest(false)
		setLanguageRequestText('')
		setLanguageRequestError(false)
		setLanguageRequestSubmitting(false)
	}, [])

	const toggleOption = (key: BpmnGenerationOptionKey, checked: boolean) => {
		onBpmnGenerationOptionsChange({
			...bpmnGenerationOptions,
			[key]: checked,
		})
	}

	const handleLanguageRequestSubmit = () => {
		const trimmed = languageRequestText.trim()
		if (!trimmed) {
			setLanguageRequestError(true)
			return
		}

		setLanguageRequestError(false)
		setLanguageRequestSubmitting(true)
		try {
			const eventId = submitAnalysisLanguageRequestFeedback(trimmed, '', user)
			if (!eventId) {
				toast.error(t('languageRequest.error'))
				return
			}

			toast.success(t('languageRequest.success'))
			resetLanguageRequestForm()
		} catch {
			toast.error(t('languageRequest.error'))
		} finally {
			setLanguageRequestSubmitting(false)
		}
	}

	return (
		<div className="flex-1 flex flex-col min-h-0 space-y-4">
			<div>
				<div className="flex items-center gap-2 mb-1.5">
					<h2 className="text-lg font-semibold text-foreground">
						{t('pages.projectDetail.modal.step3.title')}
					</h2>
					<Tooltip>
						<TooltipTrigger asChild>
							<Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
						</TooltipTrigger>
						<TooltipContent className="max-w-sm text-left">
							<p className="text-pretty">{t('pages.projectDetail.modal.step3.tooltip')}</p>
						</TooltipContent>
					</Tooltip>
				</div>
			</div>

			<div className="flex-1 overflow-y-auto overflow-x-hidden px-1 py-1">
				<div className="space-y-3">
					<div className="mt-2 space-y-4">
						<div>
							<h3 className="text-base font-medium text-foreground mb-1.5">
								{t('pages.projectDetail.modal.step3.selectTemplateTitle')}
							</h3>
							<div>
								<div
									role="button"
									tabIndex={0}
									onClick={() => onTemplateSelect(selectedTemplate === 'bpmn' ? null : 'bpmn')}
									onKeyDown={(event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault()
											onTemplateSelect(selectedTemplate === 'bpmn' ? null : 'bpmn')
										}
									}}
									className={cn(
										'p-3 border-2 rounded-xl transition-all text-left w-full cursor-pointer',
										selectedTemplate === 'bpmn'
											? 'border-primary bg-primary/10'
											: 'border-border hover:border-primary/50 bg-card'
									)}
								>
									<div className="flex flex-col items-center gap-1.5">
										<Workflow
											className={cn(
												'w-7 h-7',
												selectedTemplate === 'bpmn' ? 'text-primary' : 'text-muted-foreground'
											)}
										/>
										<span
											className={cn(
												'font-medium text-sm',
												selectedTemplate === 'bpmn' ? 'text-primary' : 'text-foreground'
											)}
										>
											{t('pages.projectDetail.modal.bpmn')}
										</span>
										<span className="text-xs text-muted-foreground text-center">
											{t('pages.projectDetail.modal.bpmnDescription')}
										</span>
									</div>
								</div>

								{selectedTemplate === 'bpmn' && (
									<Collapsible open={optionsOpen} onOpenChange={setOptionsOpen} className="mt-3 w-full">
										<div
											role="button"
											tabIndex={0}
											aria-expanded={optionsOpen}
											aria-label={t('pages.projectDetail.modal.bpmnOptions.trigger')}
											onClick={() => setOptionsOpen((open) => !open)}
											onKeyDown={(event) => {
												if (event.key === 'Enter' || event.key === ' ') {
													event.preventDefault()
													setOptionsOpen((open) => !open)
												}
											}}
											className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-border bg-background px-3 py-2 hover:border-primary/50"
										>
											<div className="inline-flex min-w-0 flex-1 flex-wrap items-center gap-2">
												<span className="text-left text-sm">
													{t('pages.projectDetail.modal.bpmnOptions.trigger')}
													{enabledCount > 0 && (
														<span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-xs text-primary">
															{enabledCount}
														</span>
													)}
												</span>
												<BetaBadge />
												<div onClick={(event) => event.stopPropagation()}>
													<FeedbackBadge component="bpmn-enrichment-options" />
												</div>
											</div>
											<ChevronDown
												className={cn('h-4 w-4 shrink-0 transition-transform', optionsOpen && 'rotate-180')}
											/>
										</div>
										<CollapsibleContent className="mt-2 space-y-3 rounded-lg border border-border bg-background p-3">
											{BPMN_OPTION_GROUPS.map(({ groupKey, optionKeys }) => (
												<div key={groupKey} className="space-y-2">
													<div className="flex items-center gap-1.5">
														<p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
															{t(`pages.projectDetail.modal.bpmnOptions.groups.${groupKey}` as never)}
														</p>
														<Tooltip>
															<TooltipTrigger asChild>
																<button
																	type="button"
																	className="inline-flex shrink-0 text-muted-foreground hover:text-foreground"
																	aria-label={t(
																		`pages.projectDetail.modal.bpmnOptions.groupTooltips.${groupKey}` as never
																	)}
																>
																	<Info className="h-3 w-3 cursor-help" aria-hidden />
																</button>
															</TooltipTrigger>
															<TooltipContent className="max-w-xs">
																<p>
																	{t(`pages.projectDetail.modal.bpmnOptions.groupTooltips.${groupKey}` as never)}
																</p>
															</TooltipContent>
														</Tooltip>
													</div>
													{optionKeys.map((optionKey) => (
														<div key={optionKey} className="flex items-start gap-2">
															<Checkbox
																id={`bpmn-option-${optionKey}`}
																checked={bpmnGenerationOptions[optionKey]}
																onCheckedChange={(checked) => toggleOption(optionKey, checked === true)}
															/>
															<div className="space-y-0.5">
																<Label
																	htmlFor={`bpmn-option-${optionKey}`}
																	className="text-sm font-medium leading-none cursor-pointer"
																>
																	{t(`pages.projectDetail.modal.bpmnOptions.options.${optionKey}.label` as never)}
																</Label>
																<p className="text-xs text-muted-foreground">
																	{t(
																		`pages.projectDetail.modal.bpmnOptions.options.${optionKey}.description` as never
																	)}
																</p>
															</div>
														</div>
													))}
												</div>
											))}
										</CollapsibleContent>
									</Collapsible>
								)}
							</div>
						</div>

						<div className="w-full max-w-prose space-y-2">
							<h3 className="text-base font-medium text-foreground mb-1.5">
								{t('pages.projectDetail.modal.step3.selectOutputLanguageTitle')}
							</h3>
							<div className="w-56 max-w-full">
								<LanguageSwitcher
									value={analysisLanguage}
									onChange={onLanguageChange}
									mode="analysis-language"
								/>
							</div>
							{languageRequestAvailable && !showLanguageRequest ? (
								<UIButton
									type="button"
									variant="link"
									className="h-auto px-0 text-left"
									disabled={languageRequestSubmitting}
									onClick={() => setShowLanguageRequest(true)}
								>
									{t('languageRequest.askLanguage')}
								</UIButton>
							) : null}
							{showLanguageRequest ? (
								<div className="space-y-3 pt-1">
									<div className="space-y-2">
										<Label htmlFor="analysis-language-request">{t('languageRequest.label')}</Label>
										<Textarea
											id="analysis-language-request"
											value={languageRequestText}
											onChange={(event) => {
												setLanguageRequestText(event.target.value)
												if (languageRequestError && event.target.value.trim()) {
													setLanguageRequestError(false)
												}
											}}
											placeholder={t('languageRequest.placeholder')}
											rows={3}
											disabled={languageRequestSubmitting}
											aria-invalid={languageRequestError}
										/>
										{languageRequestError ? (
											<p className="text-sm text-destructive">{t('languageRequest.required')}</p>
										) : null}
									</div>
									<div className="flex flex-wrap justify-end gap-2">
										<UIButton
											type="button"
											variant="outline"
											disabled={languageRequestSubmitting}
											onClick={resetLanguageRequestForm}
										>
											{t('languageRequest.cancel')}
										</UIButton>
										<UIButton
											type="button"
											disabled={languageRequestSubmitting}
											onClick={handleLanguageRequestSubmit}
										>
											{t('languageRequest.submit')}
										</UIButton>
									</div>
								</div>
							) : null}
						</div>
					</div>
				</div>
			</div>

			<div className="flex gap-2">
				<UIButton
					type="button"
					onClick={onContinue}
					disabled={!selectedTemplate}
					className="flex-[2]"
					data-tour={TOUR.modalTemplateContinue}
				>
					{t('common.continue')}
				</UIButton>
			</div>
		</div>
	)
}
